/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {getAllAccountIds} from '@ion/core/src/services/FetchWorkerUtils';
import {getAdditionalFilterParams, getSelected} from '@/routes/tracking/trackingUtils';
import {getFilteredAssets, getFilterRequestParams} from '@/store/helpers';
import {globalGroupIdSelector, globalGroupStateSelector} from '@/store/selectors/global-group-chooser-selectors';
import {trackingFiltersSelector} from '@/store/selectors/tracking-selectors';

interface GetRequestParams {
  pageSize?: number;
  sort?: string;
  filter?: any;
  callback?: (params) => void;
  page?: number;
  applyDefaultFilters?: boolean;
  apiParams?: GridApiParams;
}

interface GridApiParams {
  accountIds: number[];
  globalGroupId: number | null;
  filters: any;
}

interface GridRequestParamsResult {
  apiParams: GridApiParams;
  getRequestParams: (options: GetRequestParams) => {
    params: {
      groups: string[];
      assets: string[];
      assetTypes: string[];
      assetCategories: string[];
      geoFences: string[];
      routes: string[];
      assetStatus: string[];
      geometry: any;
      sort: string;
      startRow: number;
      page: number;
      pageSize: number;
      filter: any;
    };
    callback?: (params) => void;
    filterApplied?: boolean;
  };
};

const DEFAULT_FILTERS = {
  groups          : {selected: []},
  assetTypes      : {selected: []},
  assetCategories : {selected: []},
  geoFences       : {selected: []},
  routes          : {selected: []},
  assetStatus     : {selected: []},
  assets          : {selected: []},
  devices         : {selected: []},
  boundary        : []
};

/**
 * Custom React hook for managing grid request parameters.
 * It handles the retrieval and management of filters, global group ID, and all account IDs.
 * 
 * This hook is designed to facilitate the management of parameters required for grid operations,
 * such as fetching data based on filters, sorting, pagination, and other criteria.
 * 
 * By utilizing this hook, components can easily access and manipulate parameters necessary
 * for making requests to fetch grid data. It encapsulates the logic for handling filters,
 * global group ID, and all account IDs, providing a centralized and reusable solution.
 * 
 * ```typescript
 * 
 *  import {useGridRequestParams} from './useGridRequestParams';
 * 
 *  const {apiParams, getRequestParams} = useGridRequestParams();
 * 
 * ```
 */
export const useGridRequestParams = (): GridRequestParamsResult => {
  const filters          = useSelector(trackingFiltersSelector);
  const globalGroupId    = useSelector(globalGroupIdSelector);
  const globalGroupState = useSelector(globalGroupStateSelector);

  const selectedFilters = useMemo(() => getSelected(filters, true), [filters]);

  const allAccountIds = useMemo(() => {
    if (!isNil(globalGroupId)) {
      return getAllAccountIds(globalGroupState, entityId(globalGroupId));
    };
    return [];
  }, [globalGroupState.accountHierarchy, globalGroupState.globalGroup, globalGroupId]);

  const paramsRef = useRef({filters, allAccountIds});

  useEffect(() => {
    paramsRef.current = {
      filters,
      allAccountIds
    };
  }, [selectedFilters, allAccountIds]);

  const getRequestParams = useCallback(({
    pageSize,
    sort,
    filter,
    callback,
    page,
    applyDefaultFilters = false,
    apiParams: renderApiParams
  }: GetRequestParams) => {
    // the filters and accounts are synced after each commit - params built
    // during a render pass that render's api params, or they would be built
    // from the previous filters ...
    const source = renderApiParams
      ? {filters: renderApiParams.filters, allAccountIds: renderApiParams.accountIds}
      : paramsRef.current;
    const {
      groups,
      assetTypes,
      assetCategories,
      geoFences,
      routes,
      assetStatus,
      assets: {
        selected: assetsSelected
      } = {},
      devices: {
        selected: selectedDevices
      } = {},
      boundary
    } = applyDefaultFilters ? DEFAULT_FILTERS : source.filters;
    const {statusFilter, addressFilterObj} = getAdditionalFilterParams(filter);
    const appliedAssetStatus = [...getFilterRequestParams(assetStatus.selected)];
    if (statusFilter?.length) {
      Array.isArray(statusFilter)
        ? appliedAssetStatus.push(...statusFilter)
        : appliedAssetStatus.push(statusFilter);
    }

    const includeAssets  = assetsSelected?.length;
    const includeGroups  = groups?.selected?.length;
    const includeDevices = selectedDevices?.length;
    const accountIds     = [...getFilterRequestParams(groups.selected)];
    let groupIds: any    = [];
    groupIds             = accountIds?.length > 0 ? accountIds : source.allAccountIds;
    if (!includeGroups && (includeAssets || includeDevices)) {
      groupIds =  accountIds;
    };

    const params = {
      groups          : groupIds,
      assets          : getFilteredAssets(assetsSelected, groupIds),
      assetTypes      : [...getFilterRequestParams(assetTypes.selected)],
      assetCategories : [...getFilterRequestParams(assetCategories.selected)],
      geoFences       : [...getFilterRequestParams(geoFences.selected)],
      routes          : [...getFilterRequestParams(routes.selected)],
      assetStatus     : Array.from(new Set(appliedAssetStatus)),
      geometry        : boundary,
      sort            : sort || 'eventTime desc',
      startRow        : page * pageSize,
      page            : page + 1,
      pageSize,
      filter,
      ...addressFilterObj
    };

    return {
      filterApplied : !isEmpty(filter) || !isEmpty(statusFilter) || !isEmpty(addressFilterObj),
      params,
      callback
    };
  }, []);

  const apiParams = useMemo(() => ({
    accountIds : allAccountIds,
    globalGroupId,
    filters
  }), [allAccountIds, globalGroupId, selectedFilters]);

  return {
    apiParams,
    getRequestParams
  };
};

export default useGridRequestParams;
